import { getApiClient } from '../../../api';
import { Messages } from 'primereact/messages';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage';
import { getErrorMessage } from '../../../utils/errors';
import { EditHistoryDialog } from './components/EditHistoryDialog';
import { ErrorMessage } from '../../components/ErrorMessage';
import { formatTime } from '../../utils/format';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { scrollTop } from '../../utils/scroll';
import { VoteForm } from './components/VoteForm';
import { VoteBreadCrumb } from './components/VoteBreadCrumb';

export default function VoteEdit() {
    const { municipalityId, bodyType, termId, meetingId, voteId } = useParams();

    const msgs = useRef(null);
    const currentLocale = 'cs';

    const [loading, setLoading] = useState(true);
    const [initError, setInitError] = useState(null);
    const [vote, setVote] = useState(null);
    const [memberVote, setMemberVote] = useState(null);
    const [members, setMembers] = useState([]);
    const [resultOptions, setResultOptions] = useState([]);
    const [voteOptions, setVoteOptions] = useState([]);
    const [showEditHistoryDialog, setShowEditHistoryDialog] = useState(false);

    const [voteOptionLabels, setVoteOptionLabels] = useState({});
    const [resultOptionLabels, setResultOptionLabels] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [voteData, voteOptionsData, resultOptionsData, voteOptionLabelsMap, resultOptionLabelsMap] = await Promise.all([
                    getApiClient().getAdminVoteByMunicipalityIdAndBodyAndVoteId(municipalityId, bodyType, Number(voteId)),
                    getApiClient().getDropdownVoteOptionsByMunicipalityIdAndBody(municipalityId, bodyType),
                    getApiClient().getDropdownSessionResultsByMunicipalityIdAndBody(municipalityId, bodyType),
                    getApiClient().getVoteOptionsLanguageByLang(currentLocale),
                    getApiClient().getVoteResultOptionsLanguageByLang(currentLocale)
                ]);

                setVote(voteData);
                setMembers(voteData.members);
                setVoteOptions(voteOptionsData);
                setResultOptions(resultOptionsData);
                setVoteOptionLabels(voteOptionLabelsMap.labels ?? {});
                setResultOptionLabels(resultOptionLabelsMap.labels ?? {});
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setInitError(errorMessage ?? 'Nepodařilo se načíst údaje o hlasování');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentLocale, municipalityId, voteId, bodyType]);

    const onSubmit = async (data) => {
        const requestData = {
            number: data.number,
            time: formatTime(data.time),
            result: data.result,
            item: data.item,
            valid: data.valid,
            procedural: data.procedural,
            secret: data.secret,
            minutes_url: data.minutes_url,
            member_votes: Object.entries(data.members).map(([memberId, data]) => ({
                member_id: memberId,
                option: data.option,
                in_person: !!data.in_person // can be undefined if member vote was not present in API response
            }))
        };
        if (data.secret) {
            requestData.sum_yes = data.sum_yes ?? 0;
            requestData.sum_no = data.sum_no ?? 0;
            requestData.sum_abstained = data.sum_abstained ?? 0;
            requestData.sum_did_not_vote = data.sum_did_not_vote ?? 0;
            requestData.sum_absent = data.sum_absent ?? 0;
            requestData.sum_excused = data.sum_excused ?? 0;
        }
            try {
                await getApiClient().putAdminVoteByMunicipalityIdAndBodyAndVoteId(municipalityId, bodyType, Number(voteId), requestData);
                showSuccessMessage(msgs, 'Hlasování bylo úspěšně upraveno');

                // TODO make it so updated vote gets fetched only when tracking is enabled
                // a new lightweight endpoint that returns a municipality's features will have to be added
                const updatedVote = await getApiClient().getAdminVoteByMunicipalityIdAndBodyAndVoteId(municipalityId, bodyType, Number(voteId));
                setVote(updatedVote);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se upravit hlasování', { replace: true });
        } finally {
            scrollTop();
        }
    };

    const handleClickVoteHistory = (data) => {
        setMemberVote(null);
        setShowEditHistoryDialog(true);
    };

    const handleClickMemberVoteHistory = (data) => {
        setMemberVote(data);
        setShowEditHistoryDialog(true);
    };

    const hideEditHistoryDialog = () => {
        setShowEditHistoryDialog(false);
    };

    return loading ? (
        <LoadingPage />
    ) : (
        <>
            <VoteBreadCrumb
                extraItems={[
                    {
                        label: 'Upravit',
                        url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani/${meetingId}/hlasovani/${voteId}`
                    }
                ]}
            />

            {initError ? (
                <>
                    <h1>Upravit hlasování</h1>
                    <ErrorMessage prefix='Nelze použít formulář' error={initError} />
                </>
            ) : (
                <>
                    <div className='vote-header'>
                        <h1>Upravit hlasování č. {vote.number}</h1>
                        {vote.edit_history && (
                            <span
                                className='pi pi-book vote-header-edit-icon'
                                onClick={() => handleClickVoteHistory(vote.edit_history)}
                                title="Historie úprav"
                            />
                        )}
                    </div>

                    <Messages ref={msgs} className='page__messages' />
                    <VoteForm
                        members={members}
                        resultOptions={resultOptions}
                        voteOptions={voteOptions}
                        voteOptionLabels={voteOptionLabels}
                        resultOptionLabels={resultOptionLabels}
                        vote={vote}
                        onSubmit={onSubmit}
                        onClickMemberVoteEditHistory={handleClickMemberVoteHistory}
                    />

                    <EditHistoryDialog
                        isVisible={showEditHistoryDialog}
                        onHide={hideEditHistoryDialog}
                        memberVote={memberVote}
                        vote={memberVote ? null : vote}
                    />
                </>
            )}
        </>
    );
}
